import { Fragment, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './Toast';
import SendReferralModal from './SendReferralModal';
import SendThankYouModal from './SendThankYouModal';
import PhoneActionModal from './PhoneActionModal';
import { createReferral, createMemberThankYou } from '../lib/referrals';
import { photoObjectPosition } from '../lib/members';

export default function MemberModal({ member, isOpen, onClose }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [isThankYouOpen, setIsThankYouOpen] = useState(false);
  const [isPhoneOpen, setIsPhoneOpen] = useState(false);

  const isExecutive = useMemo(() => String(member?.role ?? '').toLowerCase() === 'executive', [member?.role]);

  if (!member) return null;

  const handleDetails = () => {
    onClose?.();
    navigate(`/members/${member.id}`);
  };

  const handleSendReferral = async (payload) => {
    await createReferral(payload);
    showToast('Referral sent.', { kind: 'success' });
  };

  const handleSendThankYou = async (payload) => {
    await createMemberThankYou(payload);
    showToast('Thank you sent.', { kind: 'success' });
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                  <div className="relative">
                    {/* Close Button */}
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-500">
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    {/* Member Image */}
                    {member.photo ? (
                      <div className="aspect-[2/1] w-full overflow-hidden">
                        <img src={member.photo} alt={member.name} style={{ objectPosition: photoObjectPosition(member) }} className="w-full h-full object-cover filter grayscale" />
                      </div>
                    ) : null}

                    {/* Content */}
                    <div className="px-6 py-5">
                      {member.businessLogo ? (
                        <div className="flex justify-center mb-3">
                          <img
                            src={member.businessLogo}
                            alt={`${member.businessName || member.name || 'Member'} logo`}
                            className="h-16 w-auto max-w-[60%] object-contain"
                          />
                        </div>
                      ) : null}

                      <div className="flex justify-between items-start mb-4 gap-4">
                        <div>
                          <Dialog.Title className="text-xl font-playfair-sans text-NBNA-primary">
                            {member.businessName}
                          </Dialog.Title>
                          <p className="text-gray-600">{member.name}</p>
                        </div>
                        {isExecutive ? (
                          <span className="bg-NBNA-primary text-white text-sm px-3 py-1 rounded-full">Executive</span>
                        ) : null}
                      </div>

                      {member.shortDescription ? (
                        <p className="text-gray-700 mb-5">{member.shortDescription}</p>
                      ) : null}

                      {/* Contact Options */}
                      <div className="space-y-3">
                        <h4 className="font-playfair-sans text-NBNA-primary text-lg mb-2">Contact</h4>

                        {member.phone ? (
                          <button
                            type="button"
                            onClick={() => setIsPhoneOpen(true)}
                            className="flex items-center text-gray-600 hover:text-NBNA-primary transition-colors"
                          >
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {member.phone}
                          </button>
                        ) : null}

                        {member.email ? (
                          <a href={`mailto:${member.email}`} className="flex items-center text-gray-600 hover:text-NBNA-primary transition-colors">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {member.email}
                          </a>
                        ) : null}

                        {member.website ? (
                          <a href={member.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-600 hover:text-NBNA-primary transition-colors">
                            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            Visit Website
                          </a>
                        ) : null}
                      </div>

                      <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-3 border-t pt-4">
                        <button type="button" className="btn-secondary" onClick={handleDetails}>
                          Details
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => setIsReferralOpen(true)}>
                          Send Referral
                        </button>
                        <button type="button" className="btn-primary" onClick={() => setIsThankYouOpen(true)}>
                          Send Thank You
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <SendReferralModal
        isOpen={isReferralOpen}
        onClose={() => setIsReferralOpen(false)}
        toMember={member}
        onSubmit={async (payload) => {
          try {
            await handleSendReferral(payload);
          } catch (err) {
            console.error('[referral] failed:', err);
            showToast(err?.message ?? 'Failed to send referral', { kind: 'error' });
            throw err;
          }
        }}
      />

      <PhoneActionModal
        isOpen={isPhoneOpen}
        onClose={() => setIsPhoneOpen(false)}
        phone={member.phone}
      />

      <SendThankYouModal
        isOpen={isThankYouOpen}
        onClose={() => setIsThankYouOpen(false)}
        toMember={member}
        onSubmit={async (payload) => {
          try {
            await handleSendThankYou(payload);
          } catch (err) {
            console.error('[thank you] failed:', err);
            showToast(err?.message ?? 'Failed to send thank you', { kind: 'error' });
            throw err;
          }
        }}
      />
    </>
  );
}
